import { applyTransaction } from "@datorama/akita";
import { ApiClientQueryParams } from "@lib/ApiClient";
import ApiClientInterface from "@lib/ApiClient/ApiClientInterface";
import { Observable } from "rxjs";
import { catchError, finalize, map, tap } from "rxjs/operators";
import { Infer, create, nullable, object } from "superstruct";
import { injectable } from "tsyringe";

import { LoggerInterface } from "@interfaces/LoggerInterface";

import LeaveReviewEarningRuleSchema from "../schemas/LeaveReviewEarningRuleSchema";
import LeaveReviewEarningRuleStore from "../stores/LeaveReviewEarningRuleStore";

const LeaveReviewEarningRuleResponseSchema = object({
    rule: nullable(LeaveReviewEarningRuleSchema),
});

@injectable()
class LeaveReviewEarningRuleService {
    constructor(
        private readonly leaveReviewEarningRuleStore: LeaveReviewEarningRuleStore,
        private readonly authApiClient: ApiClientInterface,
        private readonly logger: LoggerInterface
    ) {}

    getLeaveReviewEarningRule({
        productId,
        reviewRequestToken,
    }: {
        productId: number | undefined;
        reviewRequestToken?: string;
    }): Observable<Infer<typeof LeaveReviewEarningRuleResponseSchema>> {
        this.logger.debug(
            "LeaveReviewEarningRuleService.getLeaveReviewEarningRule is started"
        );
        this.leaveReviewEarningRuleStore.setLoading(true);
        const queryParams: ApiClientQueryParams = {};

        if (productId) {
            queryParams.productId = productId;
        }

        if (reviewRequestToken) {
            queryParams.reviewRequestToken = reviewRequestToken;
        }

        return this.authApiClient
            .get({
                url: "/review/getLeaveReviewEarningRule",
                responseType: "json",
                queryParams,
            })
            .pipe(
                map((v) =>
                    create(v.body, LeaveReviewEarningRuleResponseSchema)
                ),
                catchError((error: unknown) => {
                    this.logger.debug(
                        "LeaveReviewEarningRuleService.getLeaveReviewEarningRule is errored ",
                        { error }
                    );
                    throw error;
                }),
                tap((data) => {
                    if (!data.rule) {
                        return;
                    }

                    this.logger.debug(
                        "LeaveReviewEarningRuleService.getLeaveReviewEarningRule is ready"
                    );
                    applyTransaction(() => {
                        this.leaveReviewEarningRuleStore.update((state) => ({
                            ...state,
                            leaveReviewEarningRule: data.rule,
                        }));
                    });
                }),
                finalize(() => {
                    this.leaveReviewEarningRuleStore.setLoading(false);
                })
            );
    }

    resetStore() {
        applyTransaction(() => {
            this.leaveReviewEarningRuleStore.update(() => ({
                leaveReviewEarningRule: null,
            }));
            this.leaveReviewEarningRuleStore.setLoading(false);
            this.leaveReviewEarningRuleStore.setError(null);
        });
    }
}

export default LeaveReviewEarningRuleService;
